import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { PageTitle } from '@/components/PageTitle';
import { StatCard } from '@/components/StatCard';
import { ConfirmDialog } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toaster';
import { errorText } from '@/lib/errors';
import { formatDate, formatDateTime, formatNumber } from '@/lib/format';
import { requirePermission } from '@/lib/guards';
import { client, orpc } from '@/lib/orpc';
import { useCan } from '@/lib/session';

const connectResults = ['ok', 'connected_no_accounts', 'cancelled', 'invalid', 'exchange_failed'] as const;

export const Route = createFileRoute('/_app/linkedin/')({
  validateSearch: z.object({ connect: z.enum(connectResults).optional() }),
  beforeLoad: ({ context }) => requirePermission(context.me, 'linkedin.view'),
  component: LinkedInPage
});

type Connection = Awaited<ReturnType<typeof client.linkedin.overview>>['connections'][number];

function LinkedInPage() {
  const { t } = useTranslation();
  const can = useCan();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { connect } = Route.useSearch();
  const navigate = Route.useNavigate();
  const overview = useQuery({ ...orpc.linkedin.overview.queryOptions(), refetchInterval: 15_000 });
  const accounts = useQuery(orpc.linkedin.accounts.queryOptions());
  const [disconnecting, setDisconnecting] = useState<Connection | null>(null);

  // Show the OAuth result once (StrictMode runs effects twice in dev), then drop it from the URL.
  const shown = useRef<string | null>(null);
  useEffect(() => {
    if (!connect || shown.current === connect) return;
    shown.current = connect;
    toast(connect === 'ok' ? 'success' : connect === 'connected_no_accounts' ? 'info' : 'error', t(`linkedin.connectResult.${connect}`));
    void navigate({ search: {}, replace: true });
  }, [connect, navigate, t, toast]);

  const onError = (e: unknown) => toast('error', errorText(t, e));
  const start = useMutation({
    mutationFn: () => client.linkedin.connect({}),
    onSuccess: ({ url }) => window.location.assign(url),
    onError
  });
  const sync = useMutation({
    mutationFn: (v: { id: string; kind: 'full' | 'leads' }) => client.linkedin.sync(v),
    onSuccess: () => toast('success', t('linkedin.syncQueued')),
    onError
  });
  const disconnect = useMutation({
    mutationFn: (id: string) => client.linkedin.disconnect({ id }),
    onSuccess: async () => {
      setDisconnecting(null);
      await queryClient.invalidateQueries({ queryKey: orpc.linkedin.key() });
    },
    onError
  });

  const o = overview.data;
  const manage = can('linkedin.connect');

  return (
    <div className="space-y-6 p-6">
      <PageTitle
        title={t('linkedin.title')}
        items={[{ label: t('linkedin.section') }, { label: t('linkedin.overview'), active: true }]}
        actions={
          manage && (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => start.mutate()} disabled={start.isPending}>
              <span className="iconify lucide--link size-4" />
              {o?.connections.length ? t('linkedin.reconnect') : t('linkedin.connect')}
            </button>
          )
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {(
          [
            ['accounts', o?.accounts, 'lucide--briefcase'],
            ['campaignsCount', o?.campaigns, 'lucide--megaphone'],
            ['leadsTotal', o?.leads, 'lucide--users'],
            ['leadsWeek', o?.leadsLast7Days, 'lucide--trending-up']
          ] as const
        ).map(([key, value, icon]) => (
          <StatCard key={key} icon={icon} title={t(`linkedin.${key}`)} value={value === undefined ? '…' : formatNumber(value)} />
        ))}
      </div>

      {o && o.connections.length === 0 && (
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body items-start">
            <p>{t('linkedin.noConnection')}</p>
            {manage && <p className="text-base-content/60 text-sm">{t('linkedin.connectHint')}</p>}
          </div>
        </div>
      )}

      {o?.connections.map((c) => (
        <ConnectionCard
          key={c.id}
          c={c}
          manage={manage}
          onSync={(kind) => sync.mutate({ id: c.id, kind })}
          onDisconnect={() => setDisconnecting(c)}
        />
      ))}

      {accounts.data && accounts.data.length > 0 && (
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body">
            <h2 className="card-title text-base">{t('linkedin.accounts')}</h2>
            <div className="overflow-x-auto">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>{t('linkedin.account')}</th>
                    <th>{t('linkedin.role')}</th>
                    <th>{t('linkedin.currency')}</th>
                    <th>{t('admin.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.data.map((a) => (
                    <tr key={a.id}>
                      <td>
                        <p className="font-medium">{a.name}</p>
                        <p className="text-base-content/50 font-mono text-xs">{a.id}</p>
                      </td>
                      <td className="text-sm">{a.role ?? '–'}</td>
                      <td className="text-sm">{a.currency ?? '–'}</td>
                      <td className="text-sm">{a.status ?? '–'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {can('linkedin.webhooks.manage') && <WebhooksCard />}

      <ConfirmDialog
        open={disconnecting !== null}
        danger
        busy={disconnect.isPending}
        text={disconnecting && t('linkedin.confirmDisconnect', { label: disconnecting.label })}
        onClose={() => setDisconnecting(null)}
        onConfirm={() => disconnect.mutate(disconnecting!.id)}
      />
    </div>
  );
}

function ConnectionCard({ c, manage, onSync, onDisconnect }: { c: Connection; manage: boolean; onSync: (k: 'full' | 'leads') => void; onDisconnect: () => void }) {
  const { t } = useTranslation();
  const runs = useQuery({ ...orpc.linkedin.runs.queryOptions({ input: { id: c.id } }), refetchInterval: 10_000 });
  const badge = c.status === 'active' ? 'badge-success' : c.status === 'expired' ? 'badge-warning' : 'badge-error';
  return (
    <div className="card bg-base-100 shadow-sm">
      <div className="card-body">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="card-title text-base">
              <span className="iconify lucide--linkedin size-5" />
              {c.label}
              <span className={`badge badge-soft badge-sm ${badge}`}>{t(`linkedin.status.${c.status}`)}</span>
            </h2>
            {c.lastError && <p className="text-error mt-1 text-sm">{c.lastError}</p>}
          </div>
          {manage && (
            <div className="flex flex-wrap gap-2">
              <button type="button" className="btn btn-sm btn-outline border-base-300" onClick={() => onSync('leads')} disabled={c.status !== 'active'}>
                {t('linkedin.syncLeads')}
              </button>
              <button type="button" className="btn btn-sm btn-outline border-base-300" onClick={() => onSync('full')} disabled={c.status !== 'active'}>
                {t('linkedin.syncFull')}
              </button>
              <button type="button" className="btn btn-sm btn-ghost text-error" onClick={onDisconnect}>
                {t('linkedin.disconnect')}
              </button>
            </div>
          )}
        </div>
        <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2 xl:grid-cols-3">
          <div>
            <dt className="text-base-content/60">{t('linkedin.connectedAt')}</dt>
            <dd>{formatDateTime(c.connectedAt)}</dd>
          </div>
          <div>
            <dt className="text-base-content/60">{t('linkedin.tokenValidUntil')}</dt>
            <dd>{formatDate(c.accessTokenExpiresAt)}</dd>
          </div>
          <div>
            <dt className="text-base-content/60">{t('linkedin.refreshValidUntil')}</dt>
            <dd>{c.hasRefreshToken ? formatDate(c.refreshTokenExpiresAt) : t('linkedin.noRefresh')}</dd>
          </div>
          <div>
            <dt className="text-base-content/60">{t('linkedin.lastFullSync')}</dt>
            <dd>{formatDateTime(c.lastFullSyncAt)}</dd>
          </div>
          <div>
            <dt className="text-base-content/60">{t('linkedin.lastLeadSync')}</dt>
            <dd>{formatDateTime(c.lastLeadSyncAt)}</dd>
          </div>
        </dl>
        <h3 className="mt-4 text-sm font-medium">{t('linkedin.runs')}</h3>
        {runs.data?.length === 0 && <p className="text-base-content/60 text-sm">{t('linkedin.noRuns')}</p>}
        <ul className="mt-1 space-y-1 text-sm">
          {runs.data?.slice(0, 8).map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-2">
              <span
                className={`iconify size-4 ${r.status === 'ok' ? 'lucide--circle-check text-success' : r.status === 'failed' ? 'lucide--circle-x text-error' : 'lucide--loader-circle animate-spin'}`}
              />
              <span className="w-36">{formatDateTime(r.startedAt)}</span>
              <span className="w-28">{t(`linkedin.run.${r.kind}`)}</span>
              <span className="text-base-content/60 font-mono text-xs">
                {r.error ??
                  Object.entries(r.stats)
                    .filter(([k]) => !k.startsWith('skipped'))
                    .map(([k, v]) => `${k} ${v}`)
                    .join(' · ')}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function WebhooksCard() {
  const { t } = useTranslation();
  const toast = useToast();
  const hooks = useQuery(orpc.linkedin.webhooks.queryOptions());
  const subscribe = useMutation({
    mutationFn: () => client.linkedin.subscribe({}),
    onSuccess: async (r) => {
      toast(r.failed.length ? 'error' : 'success', t('linkedin.subscribed', { created: r.created, existing: r.existing, failed: r.failed.length }));
      await hooks.refetch();
    },
    onError: (e) => toast('error', errorText(t, e))
  });
  if (!hooks.data) return null;
  return (
    <div className="card bg-base-100 shadow-sm">
      <div className="card-body">
        <h2 className="card-title text-base">{t('linkedin.webhooks')}</h2>
        <p className="text-sm">
          {t('linkedin.webhookUrl')}: <code className="font-mono text-xs">{hooks.data.webhookUrl}</code>
        </p>
        {hooks.data.reachable ? (
          <p className="text-base-content/70 text-sm">{t('linkedin.webhookSteps')}</p>
        ) : (
          <div className="alert alert-info alert-soft text-sm">{t('linkedin.webhookUnreachable')}</div>
        )}
        <ul className="mt-2 space-y-1 text-sm">
          {hooks.data.subscriptions.length === 0 && <li className="text-base-content/60">{t('linkedin.noSubscriptions')}</li>}
          {hooks.data.subscriptions.map((s) => (
            <li key={s.id} className="font-mono text-xs">
              {s.ownerUrn} · {s.leadType}
            </li>
          ))}
        </ul>
        <div>
          <button type="button" className="btn btn-sm btn-primary mt-2" disabled={!hooks.data.reachable || subscribe.isPending} onClick={() => subscribe.mutate()}>
            {t('linkedin.subscribe')}
          </button>
        </div>
      </div>
    </div>
  );
}
